import axios from 'axios';
import {
  clearStoredAuthTokens,
  emitSessionExpired,
  ensureAccessToken,
  getStoredAccessToken,
  persistTokenPair,
  refreshAccessTokenOnce,
} from './authTokens';
import { clubRequestHeaders, forgetStoredClubSlug, getCurrentClubSlug, isClubHost } from '../currentClub';

const PROD_API_ROOT = 'https://apiv2-e5ggcsg0had0hqea.westeurope-01.azurewebsites.net';
const DEV_API_ROOT = 'https://localhost:7097';

/** URL de l'API : `REACT_APP_API_URL` si défini ; sinon l'API locale en `npm start`, la prod dans `npm run build`. */
export const API_ROOT = (
  process.env.REACT_APP_API_URL || (process.env.NODE_ENV === 'development' ? DEV_API_ROOT : PROD_API_ROOT)
).replace(/\/+$/, '');

export const API_BASE_URL = `${API_ROOT}/`;

/** @deprecated Kept for import compatibility — JWT only, no shared token. */
export const API_AUTH_TOKEN = null;

/** Délai max d'une requête : sans lui, une requête bloquée (réseau du club, API qui redémarre) reste pendante
  * indéfiniment et le polling suivant s'empile derrière. */
export const API_TIMEOUT_MS = 15000;

export const v2Api = axios.create({
  baseURL: API_ROOT,
  timeout: API_TIMEOUT_MS,
  headers: {
    'Content-Type': 'application/json',
    'Host-Environment': '.net',
  },
});

v2Api.interceptors.request.use(async (config) => {
  // Club courant (multi-clubs) : sur toutes les requêtes, anonymes comprises ; aucun en-tête sans club (repli API).
  // Un `X-Club` déjà fourni par l'appel est gardé (changement de club : requête au nom du club du jeton).
  const club = clubRequestHeaders();
  if (club['X-Club'] && !config.headers?.['X-Club']) {
    config.headers = config.headers || {};
    config.headers['X-Club'] = club['X-Club'];
  }

  const skipAuth = Boolean(config?.skipAuth);
  if (skipAuth) return config;

  const access = await ensureAccessToken(API_ROOT);
  if (access) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${access}`;
  }
  return config;
});

v2Api.interceptors.response.use(
  (res) => res,
  async (error) => {
    const status = error?.response?.status;
    const config = error?.config;
    const code = error?.response?.data?.error;
    // Club mémorisé que l'API ne connaît pas (club supprimé, lien erroné) : oublié pour le prochain chargement.
    // Même chose pour un club suspendu depuis (403 `club_suspended`).
    if (((status === 400 && code === 'unknown_club') || (status === 403 && code === 'club_suspended')) && !isClubHost()) {
      forgetStoredClubSlug();
    }
    // Jeton d'un autre club que le club demandé : la session ne vaut pas pour ce club → reconnexion.
    if (status === 403 && code === 'club_mismatch' && !config?.skipAuth) {
      clearStoredAuthTokens();
      emitSessionExpired();
      return Promise.reject(error);
    }
    if (!config || status !== 401 || config._authRetry || config.skipAuth) {
      return Promise.reject(error);
    }

    config._authRetry = true;
    const refreshed = await refreshAccessTokenOnce(API_ROOT);
    if (!refreshed) {
      clearStoredAuthTokens();
      emitSessionExpired();
      return Promise.reject(error);
    }

    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${refreshed}`;
    return v2Api.request(config);
  },
);

/**
 * `fetch` des pages publiques (TV, OBS, accueil) avec le même délai max que l'axios (`API_TIMEOUT_MS`) et un
 * `signal` optionnel (AbortController du composant, annulé au démontage). Une annulation lève une AbortError.
 */
export async function fetchWithTimeout(url, { signal, timeoutMs = API_TIMEOUT_MS, ...init } = {}) {
  // Club courant (multi-clubs) : en-tête `X-Club` ajouté à toutes ces requêtes vers l'API.
  const club = clubRequestHeaders();
  if (club['X-Club']) init.headers = { ...club, ...(init.headers || {}) };
  const ctrl = new AbortController();
  const onAbort = () => ctrl.abort();
  if (signal) {
    if (signal.aborted) ctrl.abort();
    else signal.addEventListener('abort', onAbort, { once: true });
  }
  const timer = setTimeout(() => ctrl.abort(), timeoutMs);
  try {
    return await fetch(url, { ...init, signal: ctrl.signal });
  } finally {
    clearTimeout(timer);
    if (signal) signal.removeEventListener('abort', onAbort);
  }
}

/** Erreur d'annulation (démontage / délai dépassé) : à ignorer silencieusement. */
export function isAbortError(e) {
  return e?.name === 'AbortError' || e?.name === 'CanceledError' || e?.code === 'ERR_CANCELED';
}

/**
 * Anonymous season snapshot for public home / TV (no Bearer).
 * Prefer this over v2Api.get when the page must work logged-out.
 */
export async function fetchSeasonCurrentPublic({ signal } = {}) {
  try {
    const res = await fetchWithTimeout(`${API_ROOT}/Season/current`, {
      signal,
      headers: { Accept: 'application/json', 'Host-Environment': '.net' },
    });
    if (!res.ok) return null;
    return await res.json();
  } catch {
    return null;
  }
}

export async function fetchSeasonCurrent() {
  try {
    const { data } = await v2Api.get('/Season/current');
    return data;
  } catch {
    return null;
  }
}

export { persistTokenPair, getStoredAccessToken, getCurrentClubSlug };
